import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { api } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '../../components/ui/dialog';
import { Plus, Loader2, Trash2, FileText, Download, PenLine, Calendar, Users } from 'lucide-react';
import { toast } from 'sonner';
import TestManagementPanel from '../../components/TestManagementPanel';

const FTBModulePage = ({ embedded = false }) => {
  const [tests, setTests] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [testFormData, setTestFormData] = useState({ test_name: '', class_ids: [] });
  const [bulkText, setBulkText] = useState('');
  const [saving, setSaving] = useState(false);

  const getClassDisplayName = (cls) => (cls.name || '').replace(/^Class\s+/i, '');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [testsRes, classesRes] = await Promise.all([
        api.get('/tests?module_type=ftb'),
        api.get('/classes'),
      ]);
      setTests(testsRes.data);
      setClasses(classesRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkImport = async (classId) => {
    if (!bulkText) {
      toast.error('Please enter questions');
      return;
    }

    try {
      const lines = bulkText.trim().split('\n');
      const questionIds = [];

      for (const line of lines) {
        if (!line.trim()) continue;
        // Format: Sentence with ___blanks___ | answer1, answer2, answer3 | explanation (optional)
        const parts = line.split('|').map(p => p.trim());
        if (parts.length < 2) continue;
        
        const questionText = parts[0]; // Sentence with ___ for blanks
        const correctAnswersStr = parts[1]; // Comma-separated correct answers
        const explanation = parts.length >= 3 ? parts[2] : null;

        // Parse correct answers
        const correctAnswers = correctAnswersStr.split(',').map(a => a.trim()).filter(a => a);
        if (correctAnswers.length === 0) continue;

        const response = await api.post('/questions', {
          question_text: questionText,
          correct_answers: correctAnswers,
          options: null, // No word bank
          explanation: explanation || null,
          class_id: classId,
          module_type: 'ftb'
        });
        questionIds.push(response.data.id || response.data);
      }
      return questionIds;
    } catch (err) {
      throw err;
    }
  };

  const handleCreateTest = async (e) => {
    e.preventDefault();
    if (!testFormData.test_name || testFormData.class_ids.length === 0 || !bulkText) {
      toast.error('Please fill test name, classes, and questions');
      return;
    }

    const questionClassId = testFormData.class_ids[0];

    setSaving(true);
    try {
      const questionIds = await handleBulkImport(questionClassId);
      if (!questionIds || questionIds.length === 0) {
        toast.error('No valid questions found. Check the format.');
        setSaving(false);
        return;
      }
      
      const response = await api.post('/tests', {
        test_name: testFormData.test_name,
        module_type: 'ftb',
        section_ids: testFormData.class_ids,
        question_ids: questionIds
      });
      toast.success(`${response.data.test_number} created with ${questionIds.length} questions`);
      resetForm();
      setTestDialogOpen(false);
      setBulkText('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create test');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTest = async (e) => {
    e.preventDefault();
    if (!editingTest) return;
    if (!testFormData.test_name || testFormData.class_ids.length === 0) {
      toast.error('Please fill required fields');
      return;
    }
    setSaving(true);
    try {
      let questionIds = editingTest.questions.map(q => q.id);
      if (bulkText) {
        const questionClassId = testFormData.class_ids[0];
        const newIds = await handleBulkImport(questionClassId);
        questionIds = [...questionIds, ...(newIds || [])];
      }
      
      await api.put(`/tests/${editingTest.id}`, {
        test_name: testFormData.test_name,
        section_ids: testFormData.class_ids,
        question_ids: questionIds
      });
      toast.success('Test updated');
      resetForm();
      setTestDialogOpen(false);
      setBulkText('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update test');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setTestFormData({ test_name: '', class_ids: [] });
    setEditingTest(null);
  };

  const handleDeleteTest = async (testId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this test?')) return;
    
    try {
      await api.delete(`/tests/${testId}`);
      toast.success('Test deleted successfully');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete test');
    }
  };

  const handleDeleteQuestion = async (testId, questionId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to remove this question from the test?')) return;

    try {
      const test = await api.get(`/tests/${testId}`);
      const updatedQuestionIds = test.data.questions
        .filter(q => q.id !== questionId)
        .map(q => q.id);

      await api.put(`/tests/${testId}`, {
        question_ids: updatedQuestionIds
      });

      toast.success('Question removed from test');
      if (editingTest && editingTest.id === testId) {
        openTestDialog(testId);
      }
      fetchData();
    } catch (err) {
      toast.error('Failed to remove question');
    }
  };

  const openTestDialog = async (testId) => {
    try {
      const response = await api.get(`/tests/${testId}`);
      const testDetails = response.data;
      
      setEditingTest(testDetails);
      const classIds = (testDetails.classes || []).map(c => c.id).filter(Boolean);
      setTestFormData({
        test_name: testDetails.test_name,
        class_ids: classIds
      });
      setBulkText('');
      setTestDialogOpen(true);
    } catch (err) {
      toast.error('Failed to load test details');
    }
  };

  const handleExportTest = async (testId, e) => {
    e.stopPropagation();
    try {
      const response = await api.get(`/tests/${testId}`);
      const testData = response.data;
      
      let exportText = `Test: ${testData.test_name}\n`;
      exportText += `Module: Fill the Blanks\n`;
      exportText += `Created: ${new Date(testData.created_at).toLocaleDateString()}\n`;
      exportText += `\n--- Questions ---\n\n`;
      
      testData.questions.forEach((q, idx) => {
        exportText += `${idx + 1}. ${q.question_text}\n`;
        const answers = q.correct_answers || [];
        exportText += `   Answers: ${answers.join(', ')}\n`;
        if (q.explanation) {
          exportText += `   Explanation: ${q.explanation}\n`;
        }
        exportText += `\n`;
      });

      const blob = new Blob([exportText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${testData.test_name.replace(/\s+/g, '_')}_FTB.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Test exported successfully');
    } catch (err) {
      toast.error('Failed to export test');
    }
  };

  const toggleClass = (classId) => {
    setTestFormData(prev => ({
      ...prev,
      class_ids: prev.class_ids.includes(classId)
        ? prev.class_ids.filter(id => id !== classId)
        : [...prev.class_ids, classId]
    }));
  };

  const getTestClassesDisplay = (test) => {
    if (!test.classes?.length) return 'No classes';
    return test.classes.map(s => (s.class_name || s.name || '').replace(/^Class\s+/i, '')).join(', ');
  };

  // Count blanks in a question text
  const countBlanks = (text) => {
    const matches = text.match(/___+/g);
    return matches ? matches.length : 0;
  };

  const content = (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Fill the Blanks Test
          </h1>
          <p className="text-slate-500">
            Create and manage fill-in-the-blank tests
          </p>
        </div>
        
        <Button 
          className="bg-orange-600 hover:bg-orange-700"
          onClick={() => {
            setEditingTest(null);
            setTestFormData({ test_name: '', class_ids: [] });
            setBulkText('');
            setTestDialogOpen(true);
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Test
        </Button>
      </div>

      {/* Test Dialog (Create/Edit) */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTest ? `Edit Test: ${editingTest.test_number}` : 'Create New Fill the Blanks Test'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={editingTest ? handleUpdateTest : handleCreateTest} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="testName">Test Name / Topic *</Label>
              <Input
                id="testName"
                value={testFormData.test_name}
                onChange={(e) => setTestFormData({ ...testFormData, test_name: e.target.value })}
                placeholder="e.g., Grammar - Present Tense Blanks"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Assign to Classes *</Label>
                <button type="button" onClick={() => setTestFormData(prev => ({ ...prev, class_ids: prev.class_ids.length === classes.length ? [] : classes.map(c => c.id) }))} className="text-xs text-orange-500 hover:text-orange-700 dark:text-orange-400 font-medium underline-offset-2 hover:underline">
                  {testFormData.class_ids.length === classes.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="border rounded-lg p-4 max-h-52 overflow-y-auto">
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {classes.map(cls => {
                    const dn = getClassDisplayName(cls);
                    const sel = testFormData.class_ids.includes(cls.id);
                    return (
                      <label key={cls.id} className={`flex items-center justify-center p-2 rounded-lg border-2 cursor-pointer text-sm font-semibold transition-all ${sel ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' : 'border-slate-200 dark:border-slate-700 hover:border-orange-300'}`}>
                        <input type="checkbox" className="sr-only" checked={sel} onChange={() => toggleClass(cls.id)} />
                        {dn}
                      </label>
                    );
                  })}
                </div>
              </div>
              <p className="text-xs text-slate-500">{testFormData.class_ids.length} class(es) selected</p>
            </div>

            {/* Existing Questions (for edit mode) */}
            {editingTest && editingTest.questions && editingTest.questions.length > 0 && (
              <div className="space-y-2">
                <Label>Current Questions ({editingTest.questions.length})</Label>
                <div className="border rounded-lg p-4 max-h-64 overflow-y-auto bg-slate-50 dark:bg-slate-900">
                  {editingTest.questions.map((question, idx) => {
                    const answers = question.correct_answers || [];
                    return (
                      <div key={question.id} className="mb-3 p-3 bg-white dark:bg-slate-800 rounded">
                        <div className="flex items-start justify-between mb-2">
                          <span className="text-sm font-medium">#{idx + 1}: {question.question_text}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-rose-600"
                            onClick={(e) => handleDeleteQuestion(editingTest.id, question.id, e)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                        <div className="text-xs text-emerald-600 dark:text-emerald-400">
                          Answers: {answers.join(', ')}
                        </div>
                        {question.explanation && (
                          <div className="text-xs text-blue-500 mt-1">
                            Explanation: {question.explanation}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="bulkText">Questions (one per line) {!editingTest && '*'}</Label>
              <Textarea
                id="bulkText"
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder={`Format: Sentence with ___ for blanks | answer1, answer2 | explanation (optional)

Examples:
The sun ___ in the east and ___ in the west. | rises, sets
She ___ her homework every day. | does | Simple present tense
The ___ is the capital of France. | Paris`}
                rows={8}
                className="font-mono text-sm"
              />
              <p className="text-xs text-slate-500">
                {bulkText.split('\n').filter(l => l.trim()).length} question(s) to import
              </p>
              <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                <p className="text-xs text-orange-700 dark:text-orange-300 font-medium mb-1">Format Guide:</p>
                <ul className="text-xs text-orange-600 dark:text-orange-400 space-y-1">
                  <li>• Use ___ (three underscores) for each blank in the sentence</li>
                  <li>• Separate parts with | (pipe character)</li>
                  <li>• Part 1: Sentence with blanks</li>
                  <li>• Part 2: Correct answers (comma-separated, in order)</li>
                  <li>• Part 3 (optional): Explanation</li>
                </ul>
              </div>
            </div>

            <DialogFooter>
              <Button type="submit" disabled={saving} className="bg-orange-600 hover:bg-orange-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editingTest ? 'Update Test' : 'Create Test'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tests List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
        </div>
      ) : tests.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <PenLine className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No tests created yet</p>
            <p className="text-sm text-slate-400">Click "Create Test" to create your first fill-the-blanks test</p>
          </CardContent>
        </Card>
      ) : (
        <TestManagementPanel
          tests={tests}
          classes={classes}
          onRefresh={fetchData}
          moduleColor="orange"
          moduleType="ftb"
        />
      )}
    </div>
  );

  if (embedded) return content;
  return <DashboardLayout>{content}</DashboardLayout>;
};

export default FTBModulePage;
